import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { InventoryItem, Warehouse } from '../types/inventory.types';
import {
  InventoryService,
  type VariantCombinationCreateDto,
  type VariantGroupDto,
  type VariantValueDto
} from '../services';
import { VariantDictionaryModal } from './VariantDictionaryModal';
import { useAuth } from '../../../../contexts/AuthContext';
import ImageLibrarySelectionModal from '../ImageLibrarySelectionModal';

// Kullanici icin "Grup" kavrami gizli; tum tipler/degerler tek bir varsayilan
// grup uzerinde tutulur. Sozluk modali ile ayni isim olmak zorunda.
const DEFAULT_GROUP_NAME = 'Genel';

interface VariantManagementModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  warehouses?: Warehouse[];
  onCreated?: () => void;
}

// Bir varyant kombinasyonunu (tip+deger ciftleri) deterministik bir key'e cevirir.
// Tek tip veya iki tip durumlarinda da ayni mantik calisir.
const buildCombinationKey = (pairs: Array<{ type?: string; value?: string }>): string => {
  return pairs
    .filter(p => p.type && p.value)
    .map(p => `${(p.type ?? '').trim().toLowerCase()}:${(p.value ?? '').trim().toLowerCase()}`)
    .sort()
    .join('|');
};

const normalizeImageUrls = (imageUrl?: unknown, imageUrls?: unknown): string[] => {
  const fromImageUrls = Array.isArray(imageUrls)
    ? imageUrls.filter((u): u is string => typeof u === 'string' && u.trim() !== '')
    : [];

  const fromImageUrl =
    typeof imageUrl === 'string'
      ? imageUrl
          .split(',')
          .map(u => u.trim())
          .filter(Boolean)
      : [];

  const merged = [...fromImageUrls, ...fromImageUrl];
  const seen = new Set<string>();
  const result: string[] = [];
  merged.forEach(u => {
    if (!seen.has(u)) {
      seen.add(u);
      result.push(u);
    }
  });
  return result;
};

export const VariantManagementModal: React.FC<VariantManagementModalProps> = ({
  open,
  onClose,
  item,
  warehouses = [],
  onCreated
}) => {
  const [groups, setGroups] = useState<VariantGroupDto[]>([]);
  const [values, setValues] = useState<VariantValueDto[]>([]);
  const [childVariants, setChildVariants] = useState<InventoryItem[]>([]);

  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedTip1, setSelectedTip1] = useState('');
  const [selectedTip2, setSelectedTip2] = useState('');

  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  // Hucre bazinda opsiyonel miktar. Bos string => stok kaydi olusturma.
  const [cellQuantities, setCellQuantities] = useState<Record<string, string>>({});
  const [appendName, setAppendName] = useState(true);
  const [warehouseId, setWarehouseId] = useState('');
  // Tum hucrelere toplu miktar uygulamak icin yardimci alan (opsiyonel).
  const [bulkApplyQuantity, setBulkApplyQuantity] = useState<string>('');

  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDictionaryModalOpen, setIsDictionaryModalOpen] = useState(false);
  const [dictionaryInitialType, setDictionaryInitialType] = useState<string | undefined>(undefined);

  // Mevcut alt varyantlarda inline satis fiyati ve stok miktari duzenleme.
  // - priceEdits: satir id -> kullanicinin yazdigi yeni fiyat (string)
  // - stockEdits: satir id -> kullanicinin yazdigi yeni stok adedi (secili depo icin)
  const [priceEdits, setPriceEdits] = useState<Record<string, string>>({});
  const [stockEdits, setStockEdits] = useState<Record<string, string>>({});
  const [combinationImageUrls, setCombinationImageUrls] = useState<Record<string, string[]>>({});
  const [imageModalTarget, setImageModalTarget] = useState<
    { mode: 'combination'; key: string } | { mode: 'child'; childId: string } | null
  >(null);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  const { userDetails } = useAuth();
  const companyId = userDetails?.companyId;

  const parentImageUrls = useMemo(
    () => normalizeImageUrls((item as any)?.imageUrl, (item as any)?.imageUrls),
    [item]
  );

  // Modal her acilis-kapanisinda state'i sifirla.
  useEffect(() => {
    if (!open) {
      setSelectedGroupId('');
      setSelectedTip1('');
      setSelectedTip2('');
      setSelectedCells(new Set());
      setCellQuantities({});
      setAppendName(true);
      setWarehouseId('');
      setBulkApplyQuantity('');
      setPriceEdits({});
      setStockEdits({});
      setCombinationImageUrls({});
      setImageModalTarget(null);
      setSavingRowId(null);
      setDeletingRowId(null);
      setError(null);
      setSuccessMessage(null);
      setValues([]);
    }
  }, [open]);

  // Sozluk modali kapaninca: gruplar/degerler ile UI'i tazele.
  const refreshDictionaryData = async () => {
    try {
      const groupResult = await InventoryService.getVariantGroups();
      setGroups(groupResult);
      const targetGroupId =
        selectedGroupId ||
        groupResult.find(g => g.groupName === DEFAULT_GROUP_NAME)?.id ||
        groupResult[0]?.id ||
        '';
      if (targetGroupId) {
        if (targetGroupId !== selectedGroupId) setSelectedGroupId(targetGroupId);
        const valueResult = await InventoryService.getVariantValuesByGroup(targetGroupId);
        setValues(valueResult);
      }
    } catch {
      // sessizce yok say; ust scope error gosterimi yeterli
    }
  };

  // Modal acildiginda: gruplari ve mevcut alt varyantlari paralel yukle.
  useEffect(() => {
    if (!open || !item?.id) return;

    let mounted = true;
    const loadInitialData = async () => {
      try {
        setError(null);
        setLoadingGroups(true);
        setLoadingVariants(true);

        const [groupResult, variantResult] = await Promise.all([
          InventoryService.getVariantGroups(),
          InventoryService.getVariantsByParent(item.id)
        ]);

        if (!mounted) return;
        setGroups(groupResult);
        setChildVariants(variantResult as unknown as InventoryItem[]);

        // Kullaniciya grup secimini gostermiyoruz; varsayilan "Genel" grubu ya da
        // mevcut ilk grup otomatik secilsin. Hicbir grup yoksa kullanici sozluk
        // modalini acinca otomatik olusturulacak.
        const autoGroupId =
          groupResult.find(g => g.groupName === DEFAULT_GROUP_NAME)?.id ||
          groupResult[0]?.id ||
          '';
        if (autoGroupId) setSelectedGroupId(autoGroupId);
      } catch (err) {
        if (!mounted) return;
        setError('Varyant verileri yüklenirken bir hata oluştu.');
      } finally {
        if (!mounted) return;
        setLoadingGroups(false);
        setLoadingVariants(false);
      }
    };

    loadInitialData();
    return () => {
      mounted = false;
    };
  }, [open, item?.id]);

  // Grup secildiginde o gruba ait degerleri yukle ve tip seciminin sifirla.
  useEffect(() => {
    if (!open) return;
    setSelectedTip1('');
    setSelectedTip2('');
    setSelectedCells(new Set());

    if (!selectedGroupId) {
      setValues([]);
      return;
    }

    let mounted = true;
    const loadGroupValues = async () => {
      try {
        setLoadingValues(true);
        const valueResult = await InventoryService.getVariantValuesByGroup(selectedGroupId);
        if (!mounted) return;
        setValues(valueResult);
      } catch (err) {
        if (!mounted) return;
        setError('Varyant grubu değerleri yüklenemedi.');
      } finally {
        if (!mounted) return;
        setLoadingValues(false);
      }
    };

    loadGroupValues();
    return () => {
      mounted = false;
    };
  }, [open, selectedGroupId]);

  // Tip degisince hucre secimini sifirla (yanlis kombinasyon kalmasin).
  useEffect(() => {
    setSelectedCells(new Set());
    setCellQuantities({});
    setCombinationImageUrls({});
  }, [selectedTip1, selectedTip2]);

  const valueTypes = useMemo(() => {
    const typeSet = new Set(values.map(v => v.valueType).filter(Boolean));
    return Array.from(typeSet);
  }, [values]);

  const tip1Values = useMemo(
    () => values.filter(v => v.valueType === selectedTip1),
    [values, selectedTip1]
  );
  const tip2Values = useMemo(
    () => values.filter(v => v.valueType === selectedTip2),
    [values, selectedTip2]
  );

  // Mevcut alt varyantlardan kombinasyon key'lerini cikar (dublikat kontrolu icin).
  const existingCombinationKeys = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    childVariants.forEach(child => {
      const raw = (child as any).variants;
      if (!raw) return;
      let parsed: Array<{ name?: string; value?: string }> = [];
      if (Array.isArray(raw)) {
        parsed = raw;
      } else if (typeof raw === 'string') {
        try {
          parsed = JSON.parse(raw);
        } catch {
          parsed = [];
        }
      }
      const key = buildCombinationKey(
        parsed.map(p => ({ type: p.name, value: p.value }))
      );
      if (key) set.add(key);
    });
    return set;
  }, [childVariants]);

  // Hucre key olusturucu: ayni mantigi hem matriste hem gondermede kullan.
  const cellKey = (t1: string, t2?: string): string => {
    if (selectedTip2 && t2) {
      return buildCombinationKey([
        { type: selectedTip1, value: t1 },
        { type: selectedTip2, value: t2 }
      ]);
    }
    return buildCombinationKey([{ type: selectedTip1, value: t1 }]);
  };

  const toggleCell = (key: string, alreadyExists: boolean) => {
    if (alreadyExists) return;
    setSelectedCells(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        // Hucre seciminden cikarilirsa miktari da temizle.
        setCellQuantities(prevQ => {
          if (!(key in prevQ)) return prevQ;
          const copy = { ...prevQ };
          delete copy[key];
          return copy;
        });
        setCombinationImageUrls(prevImg => {
          if (!(key in prevImg)) return prevImg;
          const copy = { ...prevImg };
          delete copy[key];
          return copy;
        });
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const setCellQuantity = (key: string, value: string) => {
    // Sadece sayisal/bos kabul et (negatif/girisli durumlar onlensin).
    const cleaned = value.replace(/[^0-9.,]/g, '').replace(',', '.');
    setCellQuantities(prev => {
      if (cleaned === '') {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      }
      return { ...prev, [key]: cleaned };
    });
  };

  const applyBulkQuantity = () => {
    if (selectedCells.size === 0) return;
    const v = bulkApplyQuantity.trim();
    if (v === '') {
      // Bos uygulamak: tum secili hucrelerin miktarini temizle.
      setCellQuantities(prev => {
        const copy = { ...prev };
        selectedCells.forEach(k => delete copy[k]);
        return copy;
      });
      return;
    }
    const cleaned = v.replace(/[^0-9.,]/g, '').replace(',', '.');
    setCellQuantities(prev => {
      const copy = { ...prev };
      selectedCells.forEach(k => {
        copy[k] = cleaned;
      });
      return copy;
    });
  };

  // Tum hucreleri sec / tum secimi temizle (var olanlar haric).
  const allSelectableKeys = useMemo<string[]>(() => {
    if (!selectedTip1) return [];
    if (selectedTip2) {
      const keys: string[] = [];
      tip1Values.forEach(t1 => {
        tip2Values.forEach(t2 => {
          const k = cellKey(t1.valueName, t2.valueName);
          if (!existingCombinationKeys.has(k)) keys.push(k);
        });
      });
      return keys;
    }
    return tip1Values
      .map(t1 => cellKey(t1.valueName))
      .filter(k => !existingCombinationKeys.has(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTip1, selectedTip2, tip1Values, tip2Values, existingCombinationKeys]);

  const handleSelectAll = () => setSelectedCells(new Set(allSelectableKeys));
  const handleClearSelection = () => {
    setSelectedCells(new Set());
    setCellQuantities({});
    setCombinationImageUrls({});
  };

  // Hucre key -> okunur etiket (Mavi - 23 gibi). Liste UI'i icin kullanilir.
  const cellLabel = (key: string): string => {
    if (!selectedTip1) return key;
    if (selectedTip2) {
      for (const t1 of tip1Values) {
        for (const t2 of tip2Values) {
          if (cellKey(t1.valueName, t2.valueName) === key) {
            return `${t1.valueName} - ${t2.valueName}`;
          }
        }
      }
    } else {
      for (const t1 of tip1Values) {
        if (cellKey(t1.valueName) === key) return t1.valueName;
      }
    }
    return key;
  };

  // Hucre key -> kombinasyon "values" payload'i.
  const buildCombinationValues = (key: string) => {
    if (selectedTip2) {
      for (const t1 of tip1Values) {
        for (const t2 of tip2Values) {
          if (cellKey(t1.valueName, t2.valueName) === key) {
            return [
              { valueType: selectedTip1, valueName: t1.valueName },
              { valueType: selectedTip2, valueName: t2.valueName }
            ];
          }
        }
      }
    } else {
      for (const t1 of tip1Values) {
        if (cellKey(t1.valueName) === key) {
          return [{ valueType: selectedTip1, valueName: t1.valueName }];
        }
      }
    }
    return [];
  };

  // Matrise gore stabil sirayla secili hucrelerin listesi.
  const orderedSelectedKeys = useMemo<string[]>(() => {
    if (!selectedTip1) return [];
    const keys: string[] = [];
    if (selectedTip2) {
      tip1Values.forEach(t1 => {
        tip2Values.forEach(t2 => {
          const k = cellKey(t1.valueName, t2.valueName);
          if (selectedCells.has(k)) keys.push(k);
        });
      });
    } else {
      tip1Values.forEach(t1 => {
        const k = cellKey(t1.valueName);
        if (selectedCells.has(k)) keys.push(k);
      });
    }
    return keys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCells, selectedTip1, selectedTip2, tip1Values, tip2Values]);

  // Gonderilecek kombinasyonlar: secili her hucreyi kendi miktariyla.
  const combinationsToSend = useMemo<VariantCombinationCreateDto[]>(() => {
    if (!selectedTip1 || orderedSelectedKeys.length === 0) return [];
    return orderedSelectedKeys.map(k => {
      const qStr = cellQuantities[k];
      const qNum = qStr && qStr.trim() !== '' ? Number(qStr) : undefined;
      const comboImages = combinationImageUrls[k] || [];
      return {
        values: buildCombinationValues(k),
        warehouseId: warehouseId || undefined,
        quantity: qNum && !Number.isNaN(qNum) && qNum > 0 ? qNum : undefined,
        imageUrl: comboImages[0] || undefined,
        imageUrls: comboImages.length > 0 ? comboImages : undefined
      } as VariantCombinationCreateDto;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedSelectedKeys, cellQuantities, combinationImageUrls, warehouseId, selectedTip1, selectedTip2, tip1Values, tip2Values]);

  // Herhangi bir hucrede pozitif miktar var mi? Depo zorunlulugu icin kullanilir.
  const hasAnyQuantity = useMemo(
    () =>
      orderedSelectedKeys.some(k => {
        const v = cellQuantities[k];
        return v && v.trim() !== '' && Number(v) > 0;
      }),
    [orderedSelectedKeys, cellQuantities]
  );

  const handleCreateVariants = async () => {
    if (!item?.id) return;
    if (!selectedGroupId) {
      setError('Lütfen önce varyant grubu seçin.');
      return;
    }
    if (hasAnyQuantity && !warehouseId) {
      setError('Miktar girilen varyantlar için depo seçimi zorunludur (İleri Ayarlar).');
      return;
    }
    if (combinationsToSend.length === 0) {
      setError('En az bir hücre seçmelisiniz.');
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      setCreating(true);

      const result = await InventoryService.bulkCreateVariants({
        parentItemId: item.id,
        variantGroupId: selectedGroupId,
        appendValuesToName: appendName,
        // KALICI KURAL: varyant child stok kodu her zaman suffix alir.
        appendValuesToStockCode: true,
        warehouseId: warehouseId || undefined,
        // Global quantity gondermiyoruz; her kombinasyon kendi miktarini tasiyor.
        combinations: combinationsToSend
      });

      const variantResult = await InventoryService.getVariantsByParent(item.id);
      setChildVariants(variantResult as unknown as InventoryItem[]);
      setSelectedCells(new Set());
      setCellQuantities({});
      setCombinationImageUrls({});
      setBulkApplyQuantity('');
      setSuccessMessage(
        `${result.successCount} varyant oluşturuldu${
          result.failedCount > 0 ? `, ${result.failedCount} kayıt atlandı` : ''
        }.`
      );

      if (onCreated) onCreated();
    } catch (err: any) {
      setError(
        'Varyant oluşturma sırasında hata oluştu: ' +
          (err?.response?.data?.message || err?.message || '')
      );
    } finally {
      setCreating(false);
    }
  };

  // Mevcut alt varyantlar listesini yeniler (fiyat guncelleme sonrasi cagrilir).
  const reloadChildVariants = async () => {
    if (!item?.id) return;
    try {
      const variantResult = await InventoryService.getVariantsByParent(item.id);
      setChildVariants(variantResult as unknown as InventoryItem[]);
    } catch {
      // sessizce yok say; ust scope error gosterimi yeterli
    }
  };

  const getOwnImageUrls = (target: any): string[] =>
    normalizeImageUrls(target?.imageUrl, target?.imageUrls);

  const buildUpdateDtoForChild = (child: InventoryItem, imageUrls: string[]) => ({
    id: child.id,
    companyId,
    stockCode: child.stockCode,
    name: child.name,
    categoryId: child.categoryId || null,
    brandId: child.brandId || null,
    branchesId: (child as any).branchesId || null,
    purchasePrice: (child as any).purchasePrice ?? 0,
    purchaseCurrency: (child as any).purchaseCurrency || 'TRY',
    purchaseVendor: (child as any).purchaseVendor || null,
    purchasePrice2: (child as any).purchasePrice2 ?? null,
    purchaseCurrency2: (child as any).purchaseCurrency2 || null,
    purchaseVendor2: (child as any).purchaseVendor2 || null,
    purchasePrice3: (child as any).purchasePrice3 ?? null,
    purchaseCurrency3: (child as any).purchaseCurrency3 || null,
    purchaseVendor3: (child as any).purchaseVendor3 || null,
    salePrice: (child as any).salePrice ?? 0,
    saleCurrency: (child as any).saleCurrency || 'TRY',
    saleCustomer: (child as any).saleCustomer || null,
    salePrice2: (child as any).salePrice2 ?? null,
    saleCurrency2: (child as any).saleCurrency2 || null,
    saleCustomer2: (child as any).saleCustomer2 || null,
    salePrice3: (child as any).salePrice3 ?? null,
    saleCurrency3: (child as any).saleCurrency3 || null,
    saleCustomer3: (child as any).saleCustomer3 || null,
    listPrice: (child as any).listPrice ?? null,
    listCurrency: (child as any).listCurrency || null,
    unit: (child as any).unit || 'adet',
    canBeNegative: (child as any).canBeNegative ?? true,
    minStockLevel: (child as any).minStockLevel ?? null,
    maxStockLevel: (child as any).maxStockLevel ?? null,
    description: (child as any).description || '',
    barcode: (child as any).barcode || '',
    vatRate: (child as any).vatRate ?? 0,
    vatStatus: (child as any).vatStatus ?? 'excluded',
    sctRate: (child as any).sctRate ?? null,
    sctStatus: (child as any).sctStatus ?? null,
    imageUrl: imageUrls[0] || null,
    imageUrls,
    variants: (child as any).variants || null,
    parentItemId: (child as any).parentItemId || null,
    isVariantParent: (child as any).isVariantParent === true,
    variantGroupId: (child as any).variantGroupId || null,
    isEcommerce: (child as any).isEcommerce === true,
    printerGroup: (child as any).printerGroup || null,
    departmentNo: (child as any).departmentNo ?? null,
    itemType: (child as any).itemType || 'PRODUCT',
    isActive: (child as any).isActive !== false
  });

  // Bir satirin fiyati orijinalden farkli mi (kaydet butonunu aktiflemek icin).
  const isPriceDirty = (childId: string, originalPrice: number | undefined | null): boolean => {
    const raw = priceEdits[childId];
    if (raw === undefined) return false;
    const trimmed = raw.trim();
    if (trimmed === '') return false;
    const num = Number(trimmed);
    if (Number.isNaN(num) || num < 0) return false;
    return num !== (originalPrice ?? 0);
  };

  // Secili depodaki mevcut stok miktarini getirir (yoksa 0).
  const getCurrentStockInWarehouse = (child: InventoryItem, whId: string): number => {
    if (!whId) return 0;
    const ws = (child.warehouseStocks || []).find((w: any) => w.warehouseId === whId);
    return Number(ws?.quantity ?? 0);
  };

  const getVariantSummary = (child: InventoryItem): string => {
    const raw = (child as any)?.variants;
    if (!raw) return '-';

    let parsed: Array<{ name?: string; value?: string }> = [];
    if (Array.isArray(raw)) {
      parsed = raw;
    } else if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = [];
      }
    }

    const labels = parsed
      .filter(v => (v?.name || '').trim() !== '' && (v?.value || '').trim() !== '')
      .map(v => `${(v.name || '').trim()}: ${(v.value || '').trim()}`);

    return labels.length > 0 ? labels.join(' | ') : '-';
  };

  // Stok adedi orijinalden farkli mi (depo secilmis olmasi sart).
  const isStockDirty = (child: InventoryItem): boolean => {
    if (!warehouseId) return false;
    const raw = stockEdits[child.id];
    if (raw === undefined) return false;
    const trimmed = raw.trim();
    if (trimmed === '') return false;
    const num = Number(trimmed);
    if (Number.isNaN(num) || num < 0) return false;
    return num !== getCurrentStockInWarehouse(child, warehouseId);
  };

  // Tek "Kaydet" butonu hem fiyati hem stogu (degisiklik varsa) kaydeder.
  const handleSaveRow = async (child: InventoryItem) => {
    if (!companyId) {
      setError('Şirket bilgisi alınamadı, oturumunuzu yenileyin.');
      return;
    }

    const priceDirty = isPriceDirty(child.id, child.salePrice);
    const stockDirty = isStockDirty(child);
    if (!priceDirty && !stockDirty) return;

    // Validasyon
    let newPrice: number | null = null;
    if (priceDirty) {
      const num = Number((priceEdits[child.id] ?? '').trim());
      if (Number.isNaN(num) || num < 0) {
        setError('Geçerli bir satış fiyatı girin.');
        return;
      }
      newPrice = num;
    }

    let newStock: number | null = null;
    if (stockDirty) {
      const num = Number((stockEdits[child.id] ?? '').trim());
      if (Number.isNaN(num) || num < 0) {
        setError('Geçerli bir stok adedi girin (negatif olamaz).');
        return;
      }
      newStock = num;
    }

    setSavingRowId(child.id);
    setError(null);
    try {
      if (priceDirty && newPrice != null) {
        await InventoryService.updatePrice(
          companyId,
          child.id,
          newPrice,
          undefined,
          userDetails?.id,
          userDetails?.email
        );
      }

      if (stockDirty && newStock != null) {
        // Once depoda mevcut bir stok kaydi var mi kontrol et:
        // - Var ise: mutlak set (updateWarehouseStockQuantity) -> miktar artirilir/azaltilir
        // - Yok ise: yeni kayit (addStockToWarehouse) -> sadece pozitif miktarda olusturulabilir
        const stockExists = (child.warehouseStocks || []).some(
          (w: any) => w.warehouseId === warehouseId
        );

        if (stockExists) {
          await InventoryService.updateWarehouseStockQuantity(
            companyId,
            child.id,
            warehouseId,
            newStock
          );
        } else if (newStock > 0) {
          await InventoryService.addStockToWarehouse({
            companyId,
            productId: child.id,
            warehouseId,
            quantity: newStock
          });
        }
      }

      await reloadChildVariants();

      // Kayit basarili: bu satira ait edit state'lerini temizle.
      setPriceEdits(prev => {
        const copy = { ...prev };
        delete copy[child.id];
        return copy;
      });
      setStockEdits(prev => {
        const copy = { ...prev };
        delete copy[child.id];
        return copy;
      });

      const parts = [];
      if (priceDirty) parts.push('fiyat');
      if (stockDirty) parts.push('stok');
      setSuccessMessage(`"${child.name}" ${parts.join(' ve ')} güncellendi.`);

      if (onCreated) onCreated();
    } catch (err: any) {
      setError(
        'Güncelleme başarısız: ' +
          (err?.response?.data?.message || err?.message || '')
      );
    } finally {
      setSavingRowId(null);
    }
  };

  const handleOpenCombinationImageModal = (key: string) => {
    setImageModalTarget({ mode: 'combination', key });
  };

  const handleOpenChildImageModal = (childId: string) => {
    setImageModalTarget({ mode: 'child', childId });
  };

  const handleSaveVariantImages = async (selectedUrls: string[]) => {
    if (!imageModalTarget) return;

    if (imageModalTarget.mode === 'combination') {
      setCombinationImageUrls(prev => ({
        ...prev,
        [imageModalTarget.key]: selectedUrls
      }));
      setImageModalTarget(null);
      return;
    }

    if (!companyId) {
      setError('Şirket bilgisi alınamadı, oturumunuzu yenileyin.');
      return;
    }

    const targetChild = childVariants.find(c => c.id === imageModalTarget.childId);
    if (!targetChild) {
      setError('Varyant bulunamadı.');
      return;
    }

    setSavingRowId(targetChild.id);
    setError(null);
    try {
      const updateDto = buildUpdateDtoForChild(targetChild, selectedUrls);
      await InventoryService.updateItem(companyId, targetChild.id, updateDto);
      await reloadChildVariants();
      setSuccessMessage(`"${targetChild.name}" için varyant resimleri güncellendi.`);
      if (onCreated) onCreated();
    } catch (err: any) {
      setError(
        'Varyant resmi güncellenemedi: ' +
          (err?.response?.data?.message || err?.message || '')
      );
    } finally {
      setSavingRowId(null);
      setImageModalTarget(null);
    }
  };

  const handleDeleteVariant = async (child: InventoryItem) => {
    if (!companyId) {
      setError('Şirket bilgisi alınamadı, oturumunuzu yenileyin.');
      return;
    }

    const ok = window.confirm(`"${child.name}" varyantını silmek istediğinize emin misiniz?`);
    if (!ok) return;

    setDeletingRowId(child.id);
    setError(null);
    try {
      await InventoryService.deleteItem(companyId, child.id, userDetails?.email || userDetails?.id);
      await reloadChildVariants();
      setPriceEdits(prev => {
        const copy = { ...prev };
        delete copy[child.id];
        return copy;
      });
      setStockEdits(prev => {
        const copy = { ...prev };
        delete copy[child.id];
        return copy;
      });
      setSuccessMessage(`"${child.name}" varyantı silindi.`);
      if (onCreated) onCreated();
    } catch (err: any) {
      setError(
        'Varyant silinemedi: ' +
          (err?.response?.data?.message || err?.message || '')
      );
    } finally {
      setDeletingRowId(null);
    }
  };

  const renderMatrix = () => {
    if (!selectedTip1) {
      return (
        <Typography variant="body2" color="text.secondary" align="center">
          Önce en az bir tip seçin.
        </Typography>
      );
    }

    // Tek tip durumu: tek sutunlu basit liste.
    if (!selectedTip2) {
      return (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{selectedTip1}</TableCell>
                <TableCell align="center">Durum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tip1Values.map(t1 => {
                const key = cellKey(t1.valueName);
                const exists = existingCombinationKeys.has(key);
                const checked = exists || selectedCells.has(key);
                return (
                  <TableRow key={t1.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={checked}
                        disabled={exists}
                        onChange={() => toggleCell(key, exists)}
                      />
                    </TableCell>
                    <TableCell>{t1.valueName}</TableCell>
                    <TableCell align="center">
                      {exists ? (
                        <Chip size="small" label="Mevcut" color="default" />
                      ) : selectedCells.has(key) ? (
                        <Chip size="small" label="Oluşturulacak" color="primary" />
                      ) : (
                        <Chip size="small" label="-" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Iki tip durumu: matris (Tip1 satir, Tip2 sutun).
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: 'background.paper', fontWeight: 600 }}>
                {selectedTip1} \ {selectedTip2}
              </TableCell>
              {tip2Values.map(t2 => (
                <TableCell key={t2.id} align="center" sx={{ fontWeight: 600 }}>
                  {t2.valueName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tip1Values.map(t1 => (
              <TableRow key={t1.id} hover>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'action.hover' }}>
                  {t1.valueName}
                </TableCell>
                {tip2Values.map(t2 => {
                  const key = cellKey(t1.valueName, t2.valueName);
                  const exists = existingCombinationKeys.has(key);
                  const checked = exists || selectedCells.has(key);
                  return (
                    <TableCell
                      key={t2.id}
                      align="center"
                      sx={{
                        bgcolor: exists
                          ? 'action.disabledBackground'
                          : selectedCells.has(key)
                          ? 'primary.lighter'
                          : 'transparent',
                        cursor: exists ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => toggleCell(key, exists)}
                    >
                      <Tooltip
                        title={
                          exists
                            ? 'Bu kombinasyon zaten var'
                            : `${t1.valueName} - ${t2.valueName}`
                        }
                      >
                        <span>
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={exists}
                            onChange={() => toggleCell(key, exists)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Varyantları Yönet</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDictionaryInitialType(undefined);
                setIsDictionaryModalOpen(true);
              }}
            >
              Tip ve Değerleri Yönet
            </Button>
          </Box>
          <Alert severity="info">
            En fazla 2 tip seçin (örn. Renk x Beden), matristen oluşturmak istediğiniz hücreleri
            işaretleyin. Mevcut kombinasyonlar otomatik pasif gelir. Yeni tip/değer eklemek için
            "Tip ve Değerleri Yönet" butonunu kullanın.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Ana Ürün
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {item?.name || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stok Kodu: {item?.stockCode || '-'} &nbsp; | &nbsp; Mevcut Alt Varyant:{' '}
              {loadingVariants ? '...' : childVariants.length}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              1) Tip Seçimi
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Tip 1"
                    value={selectedTip1}
                    onChange={(e) => setSelectedTip1(e.target.value)}
                    disabled={!selectedGroupId || loadingValues || loadingGroups}
                    helperText="Matrisin satırları"
                  >
                    <MenuItem value="">-</MenuItem>
                    {valueTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Tooltip title={selectedTip1 ? `"${selectedTip1}" tipine yeni değer ekle` : 'Tip ekle / yönet'}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setDictionaryInitialType(selectedTip1 || undefined);
                        setIsDictionaryModalOpen(true);
                      }}
                      sx={{ minWidth: 40, px: 1, mt: '1px' }}
                    >
                      +
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Tip 2 (Opsiyonel)"
                    value={selectedTip2}
                    onChange={(e) => setSelectedTip2(e.target.value)}
                    disabled={!selectedTip1 || loadingValues}
                    helperText="Matrisin sütunları"
                  >
                    <MenuItem value="">-</MenuItem>
                    {valueTypes
                      .filter(type => type !== selectedTip1)
                      .map(type => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                  </TextField>
                  <Tooltip title={selectedTip2 ? `"${selectedTip2}" tipine yeni değer ekle` : 'Tip ekle / yönet'}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setDictionaryInitialType(selectedTip2 || undefined);
                        setIsDictionaryModalOpen(true);
                      }}
                      sx={{ minWidth: 40, px: 1, mt: '1px' }}
                    >
                      +
                    </Button>
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Ileri Ayarlar (Opsiyonel)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={appendName}
                        onChange={(e) => setAppendName(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Ürün adına varyant değerlerini ekle"
                  />
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Depo (Opsiyonel)"
                      value={warehouseId}
                      onChange={(e) => setWarehouseId(e.target.value)}
                      helperText="Boş bırakılırsa hiçbir varyant için stok kaydı oluşmaz"
                    >
                      <MenuItem value="">- Stok oluşturma -</MenuItem>
                      {warehouses.map(w => (
                        <MenuItem key={w.id} value={w.id}>
                          {w.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary">
                  Miktar girişi her hücre için ayrı yapılır (matris altındaki "Seçilen Kombinasyonlar" listesi).
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Divider />

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">
                2) Matris Seçimi &nbsp;
                <Chip
                  size="small"
                  label={`Seçili: ${selectedCells.size} / ${allSelectableKeys.length}`}
                  color={selectedCells.size > 0 ? 'primary' : 'default'}
                />
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAll}
                  disabled={allSelectableKeys.length === 0 || loadingValues}
                >
                  Hepsini Seç
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearSelection}
                  disabled={selectedCells.size === 0}
                >
                  Seçimi Temizle
                </Button>
              </Stack>
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              {loadingValues ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                renderMatrix()
              )}
            </Box>
          </Paper>

          {orderedSelectedKeys.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  Seçilen Kombinasyonlar ({orderedSelectedKeys.length})
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    label="Tümüne uygula"
                    value={bulkApplyQuantity}
                    onChange={(e) => setBulkApplyQuantity(e.target.value)}
                    inputProps={{ min: 0, step: '1' }}
                    sx={{ width: 140 }}
                  />
                  <Button size="small" variant="outlined" onClick={applyBulkQuantity}>
                    Uygula
                  </Button>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Depo (Opsiyonel)"
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  error={hasAnyQuantity && !warehouseId}
                  helperText={
                    hasAnyQuantity && !warehouseId
                      ? 'Miktar girdiğiniz varyantlar için depo seçimi zorunludur.'
                      : 'Boş bırakılırsa hiçbir varyant için stok kaydı oluşmaz.'
                  }
                  sx={{ minWidth: 260 }}
                >
                  <MenuItem value="">- Stok oluşturma -</MenuItem>
                  {warehouses.map(w => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <TableContainer sx={{ maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kombinasyon</TableCell>
                      <TableCell sx={{ width: 220 }}>Resim (Opsiyonel)</TableCell>
                      <TableCell sx={{ width: 180 }}>Miktar (Opsiyonel)</TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>İşlem</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderedSelectedKeys.map(k => (
                      <TableRow key={k} hover>
                        <TableCell>{cellLabel(k)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {combinationImageUrls[k]?.[0] ? (
                              <Box
                                component="img"
                                src={combinationImageUrls[k][0]}
                                alt={cellLabel(k)}
                                sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  bgcolor: 'action.hover',
                                  border: '1px dashed',
                                  borderColor: 'divider'
                                }}
                              />
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenCombinationImageModal(k)}
                            >
                              {combinationImageUrls[k]?.length ? `Resimleri Düzenle (${combinationImageUrls[k].length})` : 'Resim Ekle'}
                            </Button>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="Boş = stok oluşturma"
                            value={cellQuantities[k] ?? ''}
                            onChange={(e) => setCellQuantity(k, e.target.value)}
                            inputProps={{ min: 0, step: '1' }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            onClick={() => toggleCell(k, false)}
                          >
                            Çıkar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {childVariants.length > 0 && (
            <Accordion disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Mevcut Alt Varyantlar ({childVariants.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 1 }}>
                  <TextField
                    select
                    size="small"
                    label="Depo (Stok Adedi)"
                    value={warehouseId}
                    onChange={(e) => setWarehouseId(e.target.value)}
                    sx={{ minWidth: 260 }}
                    helperText="Stok adedi düzenlemek için depo seçin."
                  >
                    <MenuItem value="">- Depo seçin -</MenuItem>
                    {warehouses.map(w => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                {!warehouseId && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Stok miktarı depo seçilmeden düzenlenemez. Depo seçilince aktif olur.
                  </Alert>
                )}
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Stok Kodu</TableCell>
                        <TableCell>Ad</TableCell>
                        <TableCell>Özellikler</TableCell>
                        <TableCell sx={{ width: 220 }}>Varyant Resmi</TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>Satış Fiyatı</TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>
                          Stok {warehouseId ? '(Seçili Depo Adet)' : '(Adet)'}
                        </TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>Toplam Adet</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>İşlem</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {childVariants.map(c => {
                        const totalStock = (c.warehouseStocks || []).reduce(
                          (sum: number, ws: any) => sum + (Number(ws.quantity) || 0),
                          0
                        );
                        const stockInSelectedWh = warehouseId ? getCurrentStockInWarehouse(c, warehouseId) : 0;

                        const priceEditValue = priceEdits[c.id];
                        const priceDisplayValue =
                          priceEditValue !== undefined
                            ? priceEditValue
                            : c.salePrice != null
                              ? String(c.salePrice)
                              : '';

                        const stockEditValue = stockEdits[c.id];
                        const stockDisplayValue =
                          stockEditValue !== undefined
                            ? stockEditValue
                            : warehouseId
                              ? String(stockInSelectedWh)
                              : String(totalStock);

                        const priceDirty = isPriceDirty(c.id, c.salePrice);
                        const stockDirty = isStockDirty(c);
                        const dirty = priceDirty || stockDirty;
                        const isSaving = savingRowId === c.id;
                        const isDeleting = deletingRowId === c.id;
                        const ownImages = getOwnImageUrls(c as any);
                        const effectiveImages = ownImages.length > 0 ? ownImages : parentImageUrls;
                        const variantSummary = getVariantSummary(c);

                        return (
                          <TableRow key={c.id} hover>
                            <TableCell>{c.stockCode}</TableCell>
                            <TableCell>{c.name}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {variantSummary}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {effectiveImages[0] ? (
                                  <Box
                                    component="img"
                                    src={effectiveImages[0]}
                                    alt={c.name}
                                    sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: 1,
                                      bgcolor: 'action.hover',
                                      border: '1px dashed',
                                      borderColor: 'divider'
                                    }}
                                  />
                                )}
                                <Stack spacing={0}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleOpenChildImageModal(c.id)}
                                    disabled={isSaving || isDeleting}
                                  >
                                    {ownImages.length > 0 ? `Resimleri Düzenle (${ownImages.length})` : 'Resim Ekle'}
                                  </Button>
                                  {ownImages.length === 0 && parentImageUrls.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Ana ürün resmi gösteriliyor
                                    </Typography>
                                  )}
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={priceDisplayValue}
                                onChange={(e) =>
                                  setPriceEdits(prev => ({ ...prev, [c.id]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && dirty && !isSaving) {
                                    e.preventDefault();
                                    handleSaveRow(c);
                                  }
                                }}
                                inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                                sx={{ width: 120 }}
                                disabled={isSaving || isDeleting}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={stockDisplayValue}
                                onChange={(e) =>
                                  setStockEdits(prev => ({ ...prev, [c.id]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && dirty && !isSaving) {
                                    e.preventDefault();
                                    handleSaveRow(c);
                                  }
                                }}
                                inputProps={{ min: 0, step: '1', style: { textAlign: 'right' } }}
                                sx={{ width: 120 }}
                                disabled={isSaving || isDeleting || !warehouseId}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {totalStock}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSaveRow(c)}
                                disabled={!dirty || isSaving || isDeleting}
                              >
                                {isSaving ? <CircularProgress size={16} /> : 'Kaydet'}
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={() => handleDeleteVariant(c)}
                                disabled={isSaving || isDeleting}
                                sx={{ ml: 0.5 }}
                              >
                                {isDeleting ? <CircularProgress size={14} /> : 'Sil'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
        <Button
          variant="contained"
          onClick={handleCreateVariants}
          disabled={
            creating ||
            combinationsToSend.length === 0 ||
            loadingValues ||
            loadingGroups ||
            !selectedGroupId
          }
        >
          {creating
            ? 'Oluşturuluyor...'
            : `Oluştur (${combinationsToSend.length})`}
        </Button>
      </DialogActions>

      <ImageLibrarySelectionModal
        open={!!imageModalTarget}
        onClose={() => setImageModalTarget(null)}
        onSave={handleSaveVariantImages}
        companyId={companyId || null}
        productName={
          imageModalTarget?.mode === 'child'
            ? childVariants.find(c => c.id === imageModalTarget.childId)?.name || item?.name || 'Varyant'
            : imageModalTarget?.mode === 'combination'
              ? `${item?.name || 'Ürün'} - ${cellLabel(imageModalTarget.key)}`
              : item?.name || 'Ürün'
        }
        currentImageUrls={
          imageModalTarget?.mode === 'child'
            ? getOwnImageUrls(childVariants.find(c => c.id === imageModalTarget.childId) as any)
            : imageModalTarget?.mode === 'combination'
              ? (combinationImageUrls[imageModalTarget.key] || [])
              : []
        }
        productId={
          imageModalTarget?.mode === 'child'
            ? imageModalTarget.childId
            : null
        }
      />

      <VariantDictionaryModal
        open={isDictionaryModalOpen}
        initialType={dictionaryInitialType}
        onClose={() => {
          setIsDictionaryModalOpen(false);
          setDictionaryInitialType(undefined);
          // Sozlukten yeni eklenen tip/degerler matriste hemen gorunsun.
          refreshDictionaryData();
        }}
        onChanged={refreshDictionaryData}
      />
    </Dialog>
  );
};
